// @ts-nocheck — pre-existing strict-mode type narrowing issues carried over from
// the v3-N monolithic app. Behavior is correct; a future pass will tighten the
// hook signatures and remove this annotation. The split is documented in
// `docs/plans/2026-07-01-app-split.md`.
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Box, Text, Static } from 'ink';
import { InputBar } from './components/InputBar.js';
import { LiveRegion } from './components/LiveRegion.js';
import { StatusBar } from './components/StatusBar.js';
import { renderMessage, type ChatMessage } from './components/ChatStream.js';
import { formatSkillList } from './slashCommands.js';
import { listCodingSkills } from '@zelari/core/skills';
import '@zelari/core/skills/builtin/debugging';
import '@zelari/core/skills/builtin/docs';
import '@zelari/core/skills/builtin/git-ops';
import '@zelari/core/skills/builtin/planning';
import '@zelari/core/skills/builtin/refactoring';
import '@zelari/core/skills/builtin/review';
import '@zelari/core/skills/builtin/testing';
import {
  getProviderConfig,
  getActiveProvider as getActiveProviderSpec,
} from './providerConfig.js';
import { SessionJsonlWriter } from '@zelari/core/harness';
import { useSession } from './hooks/useSession.js';
import { useChatTurn } from './hooks/useChatTurn.js';
import { useSlashDispatch } from './hooks/useSlashDispatch.js';
import { useBatchedMessages } from './hooks/useBatchedMessages.js';
import { useTerminalSize } from './hooks/useTerminalSize.js';
import type { LiveState } from './hooks/chatState.js';

const MODEL = process.env.OPENAI_MODEL ?? 'grok-4';
const PROVIDER = 'openai-compatible';

/**
 * Default model per provider, used when the user runs `/login <provider>`
 * without a prior `/model <name>` call.
 */
const providerDefaults: Record<string, string> = {
  'openai-compatible': 'grok-4',
  'grok': 'grok-4',
  'minimax': 'MiniMax-chat-latest',
  'glm': 'glm-4.5',
};

/**
 * App — Ink UI shell (v0.7.0 static-scrollback layout).
 *
 * The fixed-height frame of v0.6 (root `<Box width height overflow=hidden>` +
 * `pickVisibleMessages`) is gone. Finalized messages feed Ink's `<Static>`,
 * which prints each item exactly once to real stdout so it becomes part of
 * the terminal's native scrollback. The dynamic region Ink repaints is now
 * just: the streaming tail (`<LiveRegion>`), a one-line `<StatusBar>`, and
 * the `<InputBar>`. Because that region is always a few lines tall, it can
 * never exceed the terminal height → no full-screen clear/repaint → no
 * flicker, by construction.
 *
 * v0.4.2 split still holds: App is a thin composition of focused hooks
 * (useSession, useChatTurn, useSlashDispatch).
 */
export function App(): React.ReactElement {
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [providerConfig, setProviderConfig] = useState(() => getProviderConfig());
  const [sessionStats, setSessionStats] = useState({ totalTokens: 0, totalCostUsd: 0 });
  // v0.7.0: bump on /clear to remount <Static> (resets its internal "already
  // printed" index so the ANSI-cleared scrollback stays in sync). Also bumped
  // implicitly by a sessionId change (/new).
  const [clearEpoch, setClearEpoch] = useState(0);

  const activeProviderSpec = getActiveProviderSpec();
  const activeModel = providerConfig.modelByProvider[activeProviderSpec.id];

  const session = useSession();
  const size = useTerminalSize();

  // Throttle layer for the `live` region — coalesces per-token streaming
  // updates (~50-200/sec) into ≤60 renders/sec. The finalized array uses the
  // raw setter (its appends are rare and user-facing).
  const { commit: commitLive, flush: flushLive } = useBatchedMessages<LiveState>(
    session.live,
    session.setLive,
  );

  const chatTurn = useChatTurn({
    sessionId: session.sessionId,
    writerRef: session.writerRef,
    setMessages: session.setMessages,
    commitStreaming: commitLive,
    flushStreaming: flushLive,
    setBusy,
    setSessionActive: session.setSessionActive,
    setSessionStats,
    setLive: session.setLive,
    liveRef: session.liveRef,
  });

  // /new callback: close the old writer, open a new one for the new id.
  const onNewSession = useCallback((id: string) => {
    void session.writerRef.current?.close();
    session.writerRef.current = new SessionJsonlWriter(id);
  }, [session.writerRef]);

  // /exit callback: flush the writer, then exit. 50ms lets the Ink render
  // queue drain so the last message reaches the terminal.
  const onExit = useCallback(() => {
    void session.writerRef.current?.close();
    setTimeout(() => process.exit(0), 50);
  }, [session.writerRef]);

  // /clear callback (v0.7.0): reset the live region too (pending tools /
  // streaming bubble shouldn't survive a clear) and bump the Static epoch.
  const onClear = useCallback(() => {
    session.resetTranscript();
    setClearEpoch((e) => e + 1);
  }, [session]);

  const handleSubmit = useSlashDispatch({
    skills: useMemo(() => listCodingSkills(), []),
    sessionId: session.sessionId,
    messages: session.messages,
    setMessages: session.setMessages,
    setInput,
    setBusy,
    setSessionId: session.setSessionId,
    setSessionActive: session.setSessionActive,
    setProviderConfig: (cfg) => setProviderConfig(cfg as typeof providerConfig),
    activeProviderSpec,
    activeModel,
    providerDefaults,
    harnessRef: chatTurn.harnessRef,
    setQueueCount: chatTurn.setQueueCount,
    dispatchPrompt: chatTurn.dispatchPrompt,
    dispatchCouncilPrompt: chatTurn.dispatchCouncilPrompt,
    onNewSession,
    onExit,
    onClear,
  });

  const skills = useMemo(() => listCodingSkills(), []);
  // The one-shot banner: printed once as the first Static item. Replaces the
  // persistent Header. Includes the skill list summary so the user can see
  // what's available without the 40-col Sidebar (which can't coexist with
  // native scrollback — a fixed right column would be printed into every
  // scrollback line).
  const banner = useMemo<ChatMessage>(() => {
    const skillList = formatSkillList(skills);
    return {
      id: 'banner-once',
      role: 'system',
      ts: 0,
      content:
        `zelari-code v0.7.3 — ${activeProviderSpec.id}/${activeModel}\n` +
        `${skills.length} skills available. Type /help for the list, or /skill <name>.\n` +
        `── skills ──\n${skillList}`,
    };
  }, [skills, activeProviderSpec.id, activeModel]);

  // The Static feed: banner first, then finalized messages. `key` lets /clear
  // remount Static so its internal "already printed" index resets. clearEpoch
  // is composed in so /clear forces a remount even within the same session.
  const staticKey = `${session.sessionId || 'pre-bootstrap'}-${clearEpoch}`;
  const staticItems: readonly ChatMessage[] = [banner, ...session.messages];

  return (
    <>
      <Static key={staticKey} items={staticItems}>
        {(item) => renderMessage(item)}
      </Static>
      <Box flexDirection="column" borderTop paddingX={1}>
        <LiveRegion live={session.live} busy={busy} />
        <StatusBar
          model={activeModel}
          provider={activeProviderSpec.id}
          sessionId={session.sessionId ? session.sessionId.slice(0, 8) : '...'}
          sessionActive={session.sessionActive}
          totalTokens={sessionStats.totalTokens}
          totalCostUsd={sessionStats.totalCostUsd}
          queueCount={chatTurn.queueCount}
          busy={busy}
        />
        <InputBar
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          disabled={busy}
        />
      </Box>
    </>
  );
}

// Re-export so legacy imports keep working (Task v0.4.2 audit split).
// New code should import directly from src/cli/hooks/* instead.
export { applySteerInterrupt } from './hooks/steer.js';
export {
  formatSkillCompare,
  pickCompareWinner,
  compareSkillsFromFile,
  formatSkillCompareLine,
} from './hooks/skillCompare.js';
export { computeSessionStatsDelta } from './hooks/chatStats.js';
export { eventsToMessages } from './hooks/eventsToMessages.js';
export { formatDuration } from './utils/duration.js';

// Silence the unused-vars warning for these module-level constants while
// keeping them documented at the top of the file.
void MODEL; void PROVIDER;
